import React, { useImperativeHandle, useRef } from "react"
import { CSSTransition } from "react-transition-group"
import { PropsWithStyle, StyledComponent } from "@rogue_4/types"
import anime from "animejs"
import classNames from "classnames"

import { useDivRef } from "../../hooks/useDivRef"
import { IGameCharacter } from "../../type"
import { getTransitionClassnames } from "../../utils/transition"

import styles from "./index.module.less"

export interface ICharacterRef {
    hurt: () => Promise<void>
    attack: () => Promise<void>
    heal: () => Promise<void>
    shield: () => Promise<void>
    back: () => Promise<void>
    out: () => Promise<void>
    buff: () => Promise<void>
    shake: () => Promise<void>
}

const GameCharacter = React.forwardRef<
    ICharacterRef,
    PropsWithStyle<{
        character: IGameCharacter
        active: boolean
        gray: boolean
    }>
>(({ className, style, active, character, gray }, ref) => {
    const effectRef = useDivRef()
    const totalRef = useDivRef()
    useImperativeHandle(
        ref,
        () => ({
            hurt: () => {
                const inst = anime.timeline()
                if (effectRef.current)
                    effectRef.current.style.backgroundColor = "red"
                inst.add(
                    {
                        targets: effectRef.current,
                        opacity: [0, 0.5],
                        translateZ: [0, 0],
                        easing: "easeInOutCubic",
                        duration: 150,
                    },
                    0
                ).add(
                    {
                        targets: effectRef.current,
                        opacity: [0.5, 0],
                        translateZ: [0, 0],
                        easing: "easeInCubic",
                        duration: 150,
                    },
                    200
                )
                return inst.finished
            },
            back: () => {
                const inst = anime.timeline()
                inst.add(
                    {
                        targets: effectRef.current,
                        opacity: [0, 0.5],
                        translateZ: [0, 0],
                        easing: "easeInOutCubic",
                        duration: 150,
                    },
                    0
                )
                    .add(
                        {
                            targets: effectRef.current,
                            opacity: [0.5, 0],
                            translateZ: [0, 0],
                            easing: "easeInCubic",
                            duration: 150,
                        },
                        200
                    )
                    .add(
                        {
                            targets: totalRef.current,
                            translateX: [
                                0,
                                character.key === "nymph" ? "-2rem" : "2rem",
                            ],
                            translateZ: [0, 0],
                            easing: "easeOutCubic",
                            duration: 200,
                        },
                        0
                    )
                    .add(
                        {
                            targets: totalRef.current,
                            translateX: [
                                character.key === "nymph" ? "-2rem" : "2rem",
                                0,
                            ],
                            translateZ: [0, 0],
                            easing: "easeOutCubic",
                            duration: 600,
                        },
                        700
                    )
                return inst.finished
            },
            heal: () => {
                const inst = anime.timeline()
                if (effectRef.current)
                    effectRef.current.style.backgroundColor = "white"
                inst.add(
                    {
                        targets: totalRef.current,
                        translateY: [0, "-1rem"],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 600,
                    },
                    0
                )
                    .add(
                        {
                            targets: totalRef.current,
                            translateY: ["-1rem", 0],
                            translateZ: [0, 0],
                            easing: "easeOutCubic",
                            duration: 600,
                        },
                        1200
                    )
                    .add(
                        {
                            targets: effectRef.current,
                            opacity: [0, 0.1],
                            translateZ: [0, 0],
                            easing: "easeOutCubic",
                            duration: 600,
                        },
                        0
                    )
                    .add(
                        {
                            targets: effectRef.current,
                            opacity: [0.1, 0],
                            translateZ: [0, 0],
                            easing: "easeOutCubic",
                            duration: 600,
                        },
                        1200
                    )
                return inst.finished
            },
            attack: () => {
                const inst = anime.timeline()
                inst.add(
                    {
                        targets: totalRef.current,
                        translateX: [
                            0,
                            character.key === "nymph" ? "3rem" : "-3rem",
                        ],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 200,
                    },
                    0
                ).add(
                    {
                        targets: totalRef.current,
                        translateX: [
                            character.key === "nymph" ? "3rem" : "-3rem",
                            0,
                        ],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 600,
                    },
                    700
                )
                return inst.finished
            },
            shield: () => {
                const inst = anime.timeline()
                if (effectRef.current)
                    effectRef.current.style.backgroundColor = "#00F5FF"
                inst.add(
                    {
                        targets: effectRef.current,
                        opacity: [0, 0.4],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 300,
                    },
                    0
                ).add(
                    {
                        targets: effectRef.current,
                        opacity: [0.4, 0],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 500,
                    },
                    800
                )
                return inst.finished
            },
            shake: () => {
                const inst = anime.timeline()
                if (effectRef.current)
                    effectRef.current.style.backgroundColor = "#00F5FF"
                inst.add(
                    {
                        targets: totalRef.current,
                        translateX: [0, "0.5rem"],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 50,
                    },
                    0
                )
                    .add(
                        {
                            targets: totalRef.current,
                            translateX: ["0.5rem", "-0.5rem"],
                            translateZ: [0, 0],
                            easing: "easeInOutCubic",
                            duration: 100,
                        },
                        50
                    )
                    .add(
                        {
                            targets: totalRef.current,
                            translateX: ["-0.5rem", "0.5rem"],
                            translateZ: [0, 0],
                            easing: "easeInOutCubic",
                            duration: 100,
                        },
                        150
                    )
                    .add(
                        {
                            targets: totalRef.current,
                            translateX: ["0.5rem", 0],
                            translateZ: [0, 0],
                            easing: "easeInCubic",
                            duration: 50,
                        },
                        250
                    )
                return inst.finished
            },
            out: () => {
                const inst = anime.timeline()
                inst.add(
                    {
                        targets: totalRef.current,
                        opacity: 0,
                        translateX: [
                            0,
                            character.key === "nymph" ? "-15rem" : "15rem",
                        ],
                        translateZ: [0, 0],
                        easing: "easeInCubic",
                        duration: 500,
                    },
                    0
                )

                return inst.finished
            },
            buff: () => {
                const inst = anime.timeline()
                if (effectRef.current)
                    effectRef.current.style.backgroundColor = "#00F5FF"
                inst.add(
                    {
                        targets: totalRef.current,
                        translateY: [0, "-1rem"],
                        translateZ: [0, 0],
                        easing: "easeOutCubic",
                        duration: 300,
                    },
                    0
                ).add(
                    {
                        targets: totalRef.current,
                        translateY: ["-1rem", 0],
                        translateZ: [0, 0],
                        easing: "easeInCubic",
                        duration: 300,
                    },
                    1000
                )

                return inst.finished
            },
        }),
        [character.key, effectRef, totalRef]
    )
    return (
        <div
            className={classNames(
                styles.gameCharacter,
                styles[character.key],
                character.key !== "nymph" && styles.right,
                gray && styles.gray,
                className
            )}
            style={style}
        >
            <CSSTransition
                in={active}
                classNames={getTransitionClassnames(styles)}
                timeout={{ appear: 600, enter: 600, exit: 600 }}
            >
                <div className={styles.illustContainer} ref={totalRef}>
                    <div className={styles.filterContainer}>
                        <div className={styles.final}></div>
                        <div className={styles.back}>
                            <div className={styles.inner}></div>
                        </div>
                        <div className={styles.star}></div>
                        <div className={styles.front}></div>
                        <div className={styles.face}></div>
                        <div className={styles.effect} ref={effectRef}></div>
                    </div>
                </div>
            </CSSTransition>
            <CSSTransition
                in={active}
                classNames={getTransitionClassnames(styles)}
                timeout={{ appear: 1200, enter: 1200, exit: 400 }}
            >
                <div className={styles.label}>
                    <div className={styles.filterContainer}>
                        {character.key === "kalts" && (
                            <div className={styles.decoKalts1}></div>
                        )}
                        {character.key === "nymph" && (
                            <div className={styles.decoNymph1}></div>
                        )}
                        {character.key === "nymph" && (
                            <div className={styles.decoNymph2}></div>
                        )}
                        <div className={styles.name}></div>
                        <div className={styles.decoTop}></div>
                        {character.key === "kalts" && (
                            <div className={styles.decoKalts2}></div>
                        )}
                    </div>
                </div>
            </CSSTransition>
        </div>
    )
})
GameCharacter.displayName = "GameCharacter"

export default GameCharacter
